// Aba "Backlog" — lista de melhorias/lacunas identificadas pelo Dwight,
// filtros por status/prioridade/módulo + busca textual.
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Card, Button, Tag, Input, Select, Typography, Space, Flex, App as AntApp, Empty, Radio,
} from 'antd';
import { DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { fmtDateTime } from './utils';
import type { BacklogItem } from './types';

const { Text } = Typography;

export default function BacklogTab() {
  const { modal } = AntApp.useApp();
  const [items, setItems] = useState<BacklogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState<'todos' | 'aberto' | 'em_progresso' | 'feito' | 'descartado'>('aberto');
  const [filtroPrioridade, setFiltroPrioridade] = useState<string>('');
  const [filtroModulo, setFiltroModulo] = useState<string>('');
  const [busca, setBusca] = useState('');

  useEffect(() => { carregar(); }, []);
  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await axios.get(`${API_URL}/agentes/backlog`, { headers: { Authorization: token } });
      const data = res.data.body ? JSON.parse(res.data.body) : res.data;
      setItems(Array.isArray(data) ? data : []);
    } finally { setLoading(false); }
  }

  async function mudarStatus(id: string, status: string) {
    const token = await getToken();
    await axios.put(`${API_URL}/agentes/backlog/${id}`, { status },
      { headers: { Authorization: token } });
    await carregar();
  }
  function excluir(id: string) {
    modal.confirm({
      title: 'Excluir item do backlog?', okText: 'Excluir', cancelText: 'Cancelar', okButtonProps: { danger: true },
      onOk: async () => {
        const token = await getToken();
        await axios.delete(`${API_URL}/agentes/backlog/${id}`, { headers: { Authorization: token } });
        await carregar();
      },
    });
  }

  const filtrados = items.filter(i => {
    if (filtroStatus !== 'todos' && i.status !== filtroStatus) return false;
    if (filtroPrioridade && (i as any).prioridade !== filtroPrioridade) return false;
    if (filtroModulo) {
      if (filtroModulo === 'sem-modulo' && (i as any).modulo) return false;
      if (filtroModulo !== 'sem-modulo' && (i as any).modulo !== filtroModulo) return false;
    }
    if (busca) {
      const t = busca.toLowerCase();
      const haystack = `${(i as any).titulo || ''} ${i.descricao || ''}`.toLowerCase();
      if (!haystack.includes(t)) return false;
    }
    return true;
  });
  const statusTagColor: Record<string, string> = {
    aberto: 'gold', em_progresso: 'blue', feito: 'green', descartado: 'default',
  };
  const modulosDisp = Array.from(new Set(items.map(i => (i as any).modulo).filter(Boolean))).sort();

  return (
    <div>
      <Flex align="center" gap={10} style={{ marginBottom: 12 }}>
        <Radio.Group value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)} size="small">
          {[
            { id: 'aberto', label: `Aberto (${items.filter(i => i.status === 'aberto').length})` },
            { id: 'em_progresso', label: 'Em progresso' },
            { id: 'feito', label: 'Feitos' },
            { id: 'descartado', label: 'Descartados' },
            { id: 'todos', label: `Todos (${items.length})` },
          ].map(o => <Radio.Button key={o.id} value={o.id}>{o.label}</Radio.Button>)}
        </Radio.Group>
        <Text type="secondary" italic style={{ fontSize: 11, marginLeft: 'auto' }}>
          O Dwight registra aqui automaticamente quando bate em alguma parede.
        </Text>
      </Flex>

      <Flex gap={8} wrap="wrap" style={{ marginBottom: 12 }}>
        <Input value={busca} onChange={e => setBusca(e.target.value)}
          placeholder="Buscar em título/descrição…"
          prefix={<SearchOutlined style={{ color: '#aaa' }} />} size="small"
          style={{ minWidth: 220 }} allowClear />
        <Select value={filtroPrioridade || undefined} onChange={(v) => setFiltroPrioridade(v || '')}
          placeholder="Todas prioridades" allowClear size="small" style={{ width: 160 }}
          options={[
            { label: 'Urgente', value: 'urgente' }, { label: 'Alta', value: 'alta' },
            { label: 'Média', value: 'media' }, { label: 'Baixa', value: 'baixa' },
          ]} />
        <Select value={filtroModulo || undefined} onChange={(v) => setFiltroModulo(v || '')}
          placeholder="Todos módulos" allowClear size="small" style={{ width: 160 }}
          options={[
            ...modulosDisp.map(m => ({ label: m as string, value: m as string })),
            { label: 'Sem módulo', value: 'sem-modulo' },
          ]} />
        <Text type="secondary" style={{ fontSize: 11 }}>{filtrados.length}/{items.length}</Text>
      </Flex>

      {loading ? <Card loading /> : filtrados.length === 0 ? (
        <Empty description="Backlog vazio. O Dwight ainda não identificou lacunas — bom sinal!" />
      ) : (
        <Space direction="vertical" size={10} style={{ width: '100%' }}>
          {filtrados.map(b => (
            <Card key={b.id} size="small" style={{ borderLeft: `4px solid ${
              b.status === 'aberto' ? '#B07500' : b.status === 'em_progresso' ? '#0930D0'
                : b.status === 'feito' ? '#0a7f4a' : '#888' }`
            }}>
              <Flex align="center" gap={8} wrap="wrap" style={{ marginBottom: 6 }}>
                <Tag color="default">{b.tipo}</Tag>
                <Tag color={statusTagColor[b.status]}>{b.status}</Tag>
                {b.frequencia > 1 && (
                  <Text style={{ fontSize: 10, color: '#B07500', fontWeight: 600 }}>{b.frequencia}× repetido</Text>
                )}
                <Text type="secondary" style={{ fontSize: 10, marginLeft: 'auto' }}>{fmtDateTime(b.atualizado_em)}</Text>
              </Flex>
              <Text style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>{b.descricao}</Text>
              {b.contexto?.trecho && (
                <Card size="small" style={{ background: '#FAFBFF', marginTop: 4 }} bodyStyle={{ padding: 6 }}>
                  <Text type="secondary" italic style={{ fontSize: 11 }}>Contexto: {b.contexto.trecho}</Text>
                </Card>
              )}
              <Flex gap={6} style={{ marginTop: 8 }}>
                {b.status === 'aberto' && (
                  <Button size="small" type="primary" ghost onClick={() => mudarStatus(b.id, 'em_progresso')}>→ Em progresso</Button>
                )}
                {b.status !== 'feito' && (
                  <Button size="small" style={{ color: '#0a7f4a', borderColor: '#0a7f4a' }}
                    onClick={() => mudarStatus(b.id, 'feito')}>✓ Marcar feito</Button>
                )}
                {b.status !== 'descartado' && (
                  <Button size="small" onClick={() => mudarStatus(b.id, 'descartado')}>Descartar</Button>
                )}
                <Button size="small" danger icon={<DeleteOutlined />} style={{ marginLeft: 'auto' }}
                  onClick={() => excluir(b.id)} />
              </Flex>
            </Card>
          ))}
        </Space>
      )}
    </div>
  );
}
